import { useEffect, useMemo, useState } from "react";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import dayjs from "dayjs";
import { createRow, MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";

const CiiTargets = ({ ciiTargets, shouldRefetch, refetchMonitoringData, dropdownData, vesselMstId }) => {
	const [activeAction, setactiveAction] = useState(-1);
	const [validationErrors, setValidationErrors] = useState({});
	const [fuelConsData, setFuelConsData] = useState([]);
	const [preFilldata, setPreFilldata] = useState(null);
	const [hasFetched, setHasFetched] = useState(false);
	const [requiredCIIValues, setRequiredCIIValues] = useState({});
	const selectedYear = 2018;
	const RowActions = {
		Edit: 0,
		Add: 1,
	};

	const getCiiYrsPrefilledData = async () => {
		try {
			const response = await callApi({
				url: API_ROUTES.GET_PREFILL_TO_CiiTargets(vesselMstId),
				method: "GET",
				data: {},
			});
			if (response) {
				setPreFilldata(response);
			} else {
				console.error("Prefill API returned no data", response);
			}
		} catch (error) {
			console.error("Error fetching  data", error);
		}
	};

	const fetchRequiredCII = async () => {
		try {
			const response = await callApi({
				url: API_ROUTES.SYS_CII_VALUES(vesselMstId, selectedYear),
				method: "GET",
				data: {},
			});
			if (response) {
				// Convert array to object map: { 2020: 144.244, ... }
				const ciiMap = response.reduce((acc, item) => {
					acc[item.year] = item.requiredCii;
					return acc;
				}, {});
				setRequiredCIIValues(ciiMap);
			} else {
				console.error("required Cii returned no data", response);
			}
		} catch (error) {
			console.error("Error fetching data", error);
		}
	};

	useEffect(() => {
		getCiiYrsPrefilledData();
		fetchRequiredCII();
	}, [vesselMstId]);

	useEffect(() => {
		if (shouldRefetch && !hasFetched) {
			getCiiYrsPrefilledData();
			fetchRequiredCII();
			setHasFetched(true);
		}
	}, [shouldRefetch]);

	useEffect(() => {
		if (!shouldRefetch) {
			setHasFetched(false);
		}
	}, [shouldRefetch]);

	useEffect(() => {
		if (Array.isArray(ciiTargets) && ciiTargets.length > 0) {
			const transformed = ciiTargets.map((item) => ({
				year: item.year || null,
				requiredAnnualCII: String(requiredCIIValues[item.year] || ""),
				targetedOperationalAnnualCII: item.targetedOperationalAnnualCII || "",
				targetedRating: item.targetedRating || "",
			}));
			setFuelConsData(transformed);
		} else if (preFilldata?.year && Array.isArray(preFilldata.year) && preFilldata.year.length > 0) {
			const transformed = preFilldata.year.map((year) => ({
				year: year,
				requiredAnnualCII: String(requiredCIIValues[year] || ""),
				targetedOperationalAnnualCII: "",
				targetedRating: "",
			}));
			setFuelConsData(transformed);
		} else {
			setFuelConsData([
				{
					year: null,
					requiredAnnualCII: "",
					targetedOperationalAnnualCII: "",
					targetedRating: "",
				},
			]);
		}
	}, [ciiTargets, preFilldata,vesselMstId]);

	const resetActiveRow = () => {
		setactiveAction(-1);
		setValidationErrors({});
	};
	const handleUpdate = async ({ values, table, row }) => {
		const errors = {};
		if (!values.targetedOperationalAnnualCII?.trim()) errors.targetedOperationalAnnualCII = "Field is required";
		if (!values.targetedRating?.trim()) errors.targetedRating = "Field is Required";

		if (Object.keys(errors).length > 0) {
			setValidationErrors(errors);
			return;
		}
		const updated = fuelConsData.map((item, index) => {
			if (index === row.index) {
				return {
					...item,
					year: values?.year,
					requiredAnnualCII: values?.requiredAnnualCII,
					targetedOperationalAnnualCII: values?.targetedOperationalAnnualCII,
					targetedRating: values?.targetedRating,
				};
			}
			return item;
		});
		const finalPayload = updated.map((i) => ({ ...i, index: 0 }));

		try {
			const fuelConsumptionData = await callApi({
				method: "POST",
				url: API_ROUTES.SeempIII43(vesselMstId),
				data: finalPayload,
			});
			if (fuelConsumptionData?.isSuccess) {
				emitToasterMsg("success", "Table 4.3", "has been updated successfully");
				table.setEditingRow(null);
				setactiveAction(-1);
				setFuelConsData(finalPayload);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			} else {
				emitToasterMsg("error", "Table 4.3", fuelConsumptionData?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table 4.3", error.errorMessage);
		}
	};

	const handleReviewUpdateActions = (table, row, action) => {
		if (activeAction !== -1) return;

		if (action === RowActions.Add) {
			const defaultRow = createRow(table, {
				dateOfRevision: dayjs().toISOString(),
				revisedProvision: "",
			});
			table.setCreatingRow(defaultRow);
			setactiveAction(action);
			return;
		}
		if (action === RowActions.Edit) {
			table.setEditingRow(row);
			setactiveAction(action);
		}
	};
	const safeValue = (value) => (typeof value === "string" ? value : "");
	const columns = useMemo(
		() => [
			{
				accessorKey: "year",
				header: "Year",
				enableEditing: false,
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
						verticalAlign: "middle",
					},
				},
				muiEditTextFieldProps: {
					inputProps: {
						maxLength: 4,
					},
				},
			},
			{
				accessorKey: "requiredAnnualCII",
				header: "Required annual operational CII",
				enableEditing: false,
				muiEditTextFieldProps: ({ cell }) => {
					const value = safeValue(cell.getValue());
					return {
						required: true,
						defaultValue: value,
						inputProps: { maxLength: 13 },
						error: !!validationErrors?.requiredAnnualCII,
						helperText: validationErrors?.requiredAnnualCII,
						FormHelperTextProps: { sx: { color: "#002A4E", fontSize: "0.57rem" } },
						onFocus: () => setValidationErrors((prev) => ({ ...prev, requiredAnnualCII: undefined })),
					};
				},
			},
			{
				accessorKey: "targetedOperationalAnnualCII",
				header: "Targeted operational annual CII",
				muiEditTextFieldProps: ({ cell }) => {
					const value = safeValue(cell.getValue());
					return {
						required: true,
						type: "number",
						defaultValue: value,
						inputProps: {
							step: "any",
							inputMode: "decimal",
							pattern: "[0-9]*[.,]?[0-9]*",
						},
						onKeyDown: (e) => {
							if (["e", "E", "+", "-"].includes(e.key)) {
								e.preventDefault();
							}
						},
						error: !!validationErrors?.targetedOperationalAnnualCII,
						helperText: validationErrors?.targetedOperationalAnnualCII,
						FormHelperTextProps: { sx: { color: "#002A4E", fontSize: "0.57rem" } },
						onFocus: () => setValidationErrors((prev) => ({ ...prev, targetedOperationalAnnualCII: undefined })),
					};
				},
			},
			{
				accessorKey: "targetedRating",
				header: "Targeted rating",
				editVariant: "select",
				editSelectOptions: dropdownData?.ratings
					? dropdownData.ratings.map((rating) => ({
							value: rating.code || rating.value || rating,
							label: rating.value || rating.code || rating,
					  }))
					: ["A", "B", "C", "D", "E", "Not Applicable"].map((opt) => ({
							value: opt,
							label: opt,
					  })),
				muiEditTextFieldProps: ({ row }) => ({
					required: true,
					error: !!validationErrors?.targetedRating,
					helperText: validationErrors?.targetedRating,
					onFocus: () =>
						setValidationErrors((prev) => ({
							...prev,
							targetedRating: undefined,
						})),
					SelectProps: {
						MenuProps: {
							disablePortal: false,
							disableScrollLock: true,
							container: document.body,
							PaperProps: {
								sx: { maxHeight: 300, zIndex: 1300 },
							},
						},
					},
				}),
				muiTableHeadCellProps: {
					sx: {					
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},
		],
		[validationErrors]
	);

	const table = useMaterialReactTable({
		columns,
		data: fuelConsData || [],
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: false,
		enableBottomToolbar: false,
		enableColumnActions: false,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing: true,
		enablePagination: false,
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": {
					borderRight: "none",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "520px",
			},
		},
		displayColumnDefOptions: {
			"mrt-row-actions": {
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},
		},
		muiTableBodyCellProps: ({ row, cell }) => {
			return {
				sx: {
					borderRight: "1px solid lightgray",
					"&:last-child": {
						borderRight: "none",
					},
					color: "",
				},
			};
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				"&:not(:last-of-type)": {
					borderBottom: "1px solid lightgray",
				},
				verticalAlign: "top",
			},
		}),
		muiTableHeadRowProps: {
			sx: {
				borderBottom: "2px solid lightgray",
			},
		},
		columnOrder: ["enginesOrOtherFuelOilConsumerType", "method", "description"],
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		onEditingRowSave: handleUpdate,
		renderToolbarInternalActions: ({ table }) => <></>,
		renderRowActions: ({ row, table }) => {
			return (
				<Box sx={{ display: "flex", gap: "2rem" }}>
					<Box>
						<Tooltip title="Edit" sx>
							<IconButton style={{ padding: "0px" }} onClick={() => handleReviewUpdateActions(table, row, RowActions.Edit)}>
								<EditIcon />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
			);
		},
	});

	return (
		<div className="fuel-registry-b3 mt-1">
			<MaterialReactTable table={table} />
		</div>
	);
};

export default CiiTargets;
